import React, { useEffect, useState } from 'react';
import { Anime } from '../types/anime';
import {
  malQueryTitle,
  MalSearchItem,
  rememberMalPick,
  searchMalTitles,
} from '../services/malScore';
import { Check, ExternalLink, Loader2, Search, X } from 'lucide-react';

interface MalRatingPickerProps {
  anime: Anime;
  onUpdateAnime: (updated: Anime) => void;
  onClose: () => void;
}

/** Search MAL by any name (Thai titles don't match) and pin the right entry to this anime */
export const MalRatingPicker: React.FC<MalRatingPickerProps> = ({
  anime,
  onUpdateAnime,
  onClose,
}) => {
  const [query, setQuery] = useState(() => malQueryTitle(anime.malTitle || anime.title));
  const [results, setResults] = useState<MalSearchItem[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');

  const runSearch = async (q = query) => {
    if (malQueryTitle(q).length < 2) {
      setError('พิมพ์ชื่อภาษาอังกฤษหรือโรมาจิ (ชื่อไทยค้นใน MAL ไม่ได้)');
      return;
    }
    setIsSearching(true);
    setError('');
    const items = await searchMalTitles(q);
    setIsSearching(false);
    if (!items) {
      setError('MAL ไม่ตอบกลับ (อาจโดนจำกัดชั่วคราว) ลองใหม่อีกครั้งในอีกสักครู่');
      setResults([]);
      return;
    }
    setResults(items);
  };

  useEffect(() => {
    runSearch();
  }, [anime.id]);

  const pick = (item: MalSearchItem) => {
    rememberMalPick(item);
    onUpdateAnime({
      ...anime,
      malTitle: item.name,
      score: item.score > 0 ? item.score.toFixed(2) : anime.score,
    });
    onClose();
  };

  const clearPick = () => {
    onUpdateAnime({ ...anime, malTitle: undefined });
    onClose();
  };

  return (
    <div className="rounded-2xl border border-sky-500/40 bg-sky-950/20 p-3 sm:p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs sm:text-sm font-bold text-sky-200">
          ⭐ เช็คเรตติ้ง MyAnimeList
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
          title="ปิด"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          runSearch();
        }}
        className="flex gap-2"
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ชื่อภาษาอังกฤษ / โรมาจิ เช่น Tensei shitara Ken deshita"
          className="flex-1 min-w-0 px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-700 focus:border-sky-500 outline-none text-xs sm:text-sm text-white"
        />
        <button
          type="submit"
          disabled={isSearching}
          className="px-3 py-1.5 rounded-xl bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1"
        >
          {isSearching ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Search className="w-3.5 h-3.5" />
          )}
          <span>ค้นหา</span>
        </button>
      </form>

      {error && <p className="text-[11px] text-rose-300">{error}</p>}

      {results && results.length === 0 && !error && (
        <p className="text-[11px] text-slate-400">
          ไม่พบใน MAL ลองพิมพ์ชื่อภาษาอังกฤษหรือชื่อญี่ปุ่นแบบโรมาจิ
        </p>
      )}

      {results && results.length > 0 && (
        <ul className="space-y-1.5">
          {results.map((item) => {
            const chosen = anime.malTitle === item.name;
            return (
              <li
                key={item.id}
                className={`flex items-center gap-2 p-2 rounded-xl border ${
                  chosen ? 'border-emerald-500/60 bg-emerald-950/30' : 'border-slate-800 bg-slate-950/60'
                }`}
              >
                <span className="shrink-0 w-12 text-center text-xs font-extrabold text-amber-300">
                  {item.score > 0 ? `⭐ ${item.score.toFixed(2)}` : 'N/A'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-100 line-clamp-1" title={item.name}>
                    {item.name}
                  </div>
                  <div className="text-[10px] text-slate-500">{item.year || '—'}</div>
                </div>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="เปิดหน้า MAL"
                  className="p-1.5 text-slate-400 hover:text-sky-300 rounded-lg hover:bg-slate-800"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  type="button"
                  onClick={() => pick(item)}
                  className={`px-2 py-1 rounded-lg text-[11px] font-bold flex items-center gap-1 ${
                    chosen
                      ? 'bg-emerald-600 text-white'
                      : 'bg-sky-600/80 hover:bg-sky-500 text-white'
                  }`}
                >
                  <Check className="w-3 h-3" />
                  <span>{chosen ? 'ใช้อยู่' : 'ใช้เรื่องนี้'}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {anime.malTitle && (
        <button
          type="button"
          onClick={clearPick}
          className="text-[11px] text-slate-400 hover:text-rose-300 underline"
        >
          ยกเลิกที่เลือกไว้ (กลับไปค้นอัตโนมัติจากชื่อเรื่อง)
        </button>
      )}
    </div>
  );
};
